import {Component, Fragment} from "react";
import Glob from "../../libs/Glob.mjs";
import appLib from "../../libs/appLib.mjs";
import cytoscape from "cytoscape";
import "./PeriGraphic.css";
import {Button, Input, InputGroup, InputGroupText, Nav, Navbar, NavItem, NavLink, TabContent, TabPane} from "reactstrap";
import ChevronLeftIcon from "../../media/images/ChevronLeftIcon.mjs";
import ChevronRightIcon from "../../media/images/ChevronRightIcon.mjs";
import CloseIcon from "../../media/images/CloseIcon.mjs";
import TextLabel from "../../controls/TextLabel/TextLabel.mjs";
import AppCheckboxOnIcon from "../../media/app-images/AppCheckboxOnIcon.mjs";
import AppCheckboxOffIcon from "../../media/app-images/AppCheckboxOffIcon.mjs";
import AppInfoIcon from "../../media/app-images/AppInfoIcon.mjs";
import PropTypes from "prop-types";
import PeriGraphicInfo from "./PeriGraphicNodeInfo.mjs";

const prefix = "PeriGraphic";

const nodeColors = {
	default: "#ccc",
	Soggetto: "#6c6",
	AltroSoggetto: "#6c6",
	BeneICT: "#fc6",
	ServizioEsterno: "#f6f",
	SistemaInformativo: "#cc6",
	ServizioEssenziale: "#f99",
	FunzioneEssenziale: "#f99",
	Rete:"#9cf"
}

const csStyleSheet = [
	{
		selector: 'node',
		style: {
			backgroundColor: nodeColors.default,
			borderWidth: 1,
			borderStyle: "solid",
			borderColor: "#999",
			color: "#000",
			fontSize: 11,
			fontFamily: '"Roboto Condensed",monospace',
			textWrap: 'ellipsis',
			textMaxWidth: 150,
			textValign: 'center',
			width: 24,
			height: 24,
		}
	},
	{
		selector: 'node[trigger]',
		style: {
			shape: 'rectangle',
		}
	},
	{
		selector: 'node[text]',
		style: {
			label: 'data(text)',
		}
	},
	{
		selector: 'node[type="Soggetto"],node[type="AltroSoggetto"]',
		style: {
			backgroundColor: nodeColors.Soggetto,
			borderColor: "#696",
			color: "#060",
		}
	},
	{
		selector: 'node[type="AltroSoggetto"]',
		style: {
			opacity: ".6"
		}
	},
	{
		selector: 'node[type="BeneICT"]',
		style: {
			backgroundColor: nodeColors.BeneICT,
			opacity: ".6"
		}
	},
	{
		selector: 'node[type="ServizioEsterno"]',
		style: {
			backgroundColor: nodeColors.ServizioEsterno,
		}
	},
	{
		selector: 'node[type="SistemaInformativo"]',
		style: {
			backgroundColor: nodeColors.SistemaInformativo,
		}
	},
	{
		selector: 'node[type="ServizioEssenziale"],node[type="FunzioneEssenziale"]',
		style: {
			backgroundColor: nodeColors.ServizioEssenziale,
			opacity: ".6"
		}
	},
	{
		selector: 'node[nodeType="principale"]',
		style: {
			opacity: "1"
		}
	},
	{
		selector: 'node[type="Rete"]',
		style: {
			backgroundColor: nodeColors.Rete,
		}
	},
	{
		selector: 'node[deepSelected]',
		style: {
			borderColor: "#c00"
		}
	},
	{
		selector: 'node[selected]',
		style: {
			borderWidth: 3,
			borderColor: "#c00"
		}
	},
	{
		selector: 'node[current]',
		style: {
			outlineColor: "#ff0",
			outlineWidth: 2,
			overlayColor: "#ff0",
			overlayOpacity: .25,
			overlayPadding: 12,
			textBackgroundShape: "rectangle",
			textBackgroundOpacity: .5,
			textBackgroundColor: "#ff0",
			fontWeight: "bold"
		}
	},
	{
		selector: "edge",
		style: {
			width: 1,
			lineColor: '#ccc',
			targetArrowShape: 'triangle',
			targetArrowColor: '#ccc',
			curveStyle: 'bezier',
		}
	},
	{
		selector: "edge[name]",
		style: {
			label: 'data(name)',
			color: "#999",
			fontSize: 10,
			fontFamily: '"Roboto Condensed",monospace',
			textWrap: 'ellipsis',
			textMaxWidth: 150,
		}
	},
	{
		selector: "edge[indirect]",
		style: {
			lineColor: '#ccc',
			targetArrowColor: '#ccc',
			lineStyle: 'dotted'
		}
	},
	{
		selector: 'edge[indirect][deep]',
		style: {
			lineColor: '#fc6',
			targetArrowColor: '#fc6',
		}
	},
	{
		selector: 'edge[name="erogatoDa"],edge[name="erogataDa"]',
		style: {
			lineColor: nodeColors.ServizioEssenziale,
			targetArrowColor: nodeColors.ServizioEssenziale,
		}
	},
	{
		selector: "edge[indirect][name]",
		style: {
			color: '#999'
		}
	},
	{
		selector: "edge[indirect][deep][name]",
		style: {
			color: '#c93'
		}
	},
	{
		selector: "edge[light]",
		style: {
			opacity: ".25",
		}
	},
	{
		selector: "edge[fall]",
		style: {
			opacity: ".5",
			width: 3,
			lineColor: '#c00',
			targetArrowColor: '#c00',
		}
	},
	{
		selector: "edge[fall][indirect]",
		style: {
			opacity: ".25",
			width: 3,
			lineColor: '#c00',
			targetArrowColor: '#c00',
			lineStyle: 'dotted'
		}
	},
];

const getSaved = id => {
	if ( ! id ) return {};
	let saved = Glob.getStatus('savedGraphSimulation') ?? {};
	if ( ! saved.id ) return {};
	if ( saved.id !== id ) {
		saved = { id: id };
		Glob.setStatus('savedGraphSimulation', saved);
	}
	return saved;
}

const dropSaved = () => Glob.setStatus('savedGraphSimulation');

const setAllSaved = data => {
	if ( data?.id ) Glob.setStatus('savedGraphSimulation', data);
}

const saveItem = ( id, name, value ) => {
	if ( ! id ) return;
	const saved = getSaved(id);
	saved.id = id;
	saved[name] = value;
	setAllSaved(saved);
}

const PeriGraphic = class extends Component {
	static propTypes = {
		/**
		 * @desc ID of the perimeter
		 * @desc Mandatory if "source" data is missing
		 * @type {string}
		 */
		id: PropTypes.string,
		/**
		 * @desc Source data of the perimeter
		 * @desc Automatically retrieved if "id" is provided
		 * @type {Object}
		 */
		source: PropTypes.object,
		/**
		 * @desc Alternative to "highlighted"
		 * @desc An object whose keys are the ID of highlighted nodes
		 * @type {Record<string,any>}
		 */
		selected: PropTypes.object,
		/**
		 * @desc Alternative to "selected"
		 * @desc An array of highlighted nodes ID
		 * @type {Array<string>}
		 */
		highlighted: PropTypes.arrayOf(PropTypes.string),
		/**
		 * @desc A filter function for nodes to be on display
		 * @function
		 * @param {Object} node
		 * @return {boolean}
		 */
		preFilterNodes: PropTypes.func,
		/**
		 * @desc Can select multiple nodes at the same time?
		 * @desc Defaults to false
		 * @type {boolean}
		 */
		multiSelect: PropTypes.bool,
		/**
		 * @desc Initial display level
		 * @desc Defaults to 2
		 * @type {number}
		 */
		level: PropTypes.number,
		/**
		 * Limit subject list to IDs…
		 * @type {Array<string>}
		 */
		subjects: PropTypes.arrayOf(PropTypes.string),
		/**
		 * @desc Async func: if any, return false to prevent default action
		 * @desc Use `node.data()` to retrieve node associated information.
		 * @function
		 * @async
		 * @param {Object} node The clicked node
		 * @param {Object} PeriGraphicObject The Component object
		 * @return {Promise<boolean>}
		 */
		onNodeClick: PropTypes.func,
		/**
		 * @desc Async func: if any, return false to prevent default action
		 * @desc Use `node.data()` to retrieve node associated information.
		 * @function
		 * @async
		 * @param {Object} node The clicked node
		 * @param {Object} PeriGraphicObject The Component object
		 * @return {Promise<boolean>}
		 */
		onNodeDblClick: PropTypes.func,
		/**
		 * @desc Async func: if any, return false to prevent default action
		 * @desc Use `node.data()` to retrieve node associated information.
		 * @function
		 * @async
		 * @param {Object} node The clicked node
		 * @param {Object} PeriGraphicObject The Component object
		 * @return {Promise<boolean>}
		 */
		onNodeRightClick: PropTypes.func,
		triggers: PropTypes.arrayOf(PropTypes.string),
		/**
		 * @type {Array<{source:string,target:string}>}
		 */
		bys: PropTypes.arrayOf(PropTypes.obj),
		simul: PropTypes.string,
	};
	constructor(props) {
		super(props);
		const saved = getSaved(props.simul);
		this.state = {
			highlighted: [],
			currentSelected: [],
			multiSelect: !! props.multiSelect,
			source: props.source || false,
			data: props.data || false,
			level: saved.level ?? props.level ?? 2,
			csStyleSheet: props.csStyleSheet ?? csStyleSheet,
			nodePosition: saved.nodePosition ?? {}
		}
		if ( props.selected && typeof props.selected === 'object' && Object.keys(props.selected).length > 0 ) {
			this.state.highlighted = Object.keys(props.selected);
		} else if ( props.highlighted && Array.isArray(props.highlighted) && props.highlighted.length > 0 ) {
			this.state.highlighted = props.highlighted;
		}
		this.uid = Glob.generateUid(prefix);
		this.loadSource = this.loadSource.bind(this);
		this.preliminaryParse = this.preliminaryParse.bind(this);
		this.parse = this.parse.bind(this);
		this.parseNodes = this.parseNodes.bind(this);
		this.parseEdges = this.parseEdges.bind(this);
		this.customParseNodes = this.customParseNodes.bind(this);
		this.customParseEdges = this.customParseEdges.bind(this);
		this.postParseNodes = this.postParseNodes.bind(this);
		this.postParseEdges = this.postParseEdges.bind(this);
		this.getSourceChildren = this.getSourceChildren.bind(this);
		this.getSourceParents = this.getSourceParents.bind(this);
		this.renderCytoscape = this.renderCytoscape.bind(this);
		this.levelSelector = this.levelSelector.bind(this);
		this.nodeTypeSelector = this.nodeTypeSelector.bind(this);
		this.nodeTypeSelect = this.nodeTypeSelect.bind(this);
		this.info = this.info.bind(this);
		this.customInfo = this.customInfo.bind(this);
		this.toggleInfo = this.toggleInfo.bind(this);
		this.nodeElementSelected = this.nodeElementSelected.bind(this);
		this.onNodeClick = this.onNodeClick.bind(this);
		this.onNodeDblClick = this.onNodeDblClick.bind(this);
		this.onNodeRightClick = this.onNodeRightClick.bind(this);
		this.nodeInfo = this.nodeInfo.bind(this);
		this.toggleSubjectVisibility = this.toggleSubjectVisibility.bind(this);
	}
	componentDidMount() {
		if ( this.state.source ) {
			this.preliminaryParse( this.state.source );
		} else {
			setTimeout( this.loadSource )
		}
	}
	loadSource(){
		appLib.api('perimetri/graph', {id: this.props.id}).then( res =>{
			this.preliminaryParse(res);
		}).catch( err =>{
			console.log( err );
		})
	}
	preliminaryParse( data ) {
		const id2indexMap = {};
		const id2subjectMap = {};
		const nodeTypes = [];
		let maxLevel = this.state.level;
		let currentLevel = this.state.level;
		const known = {};
		const highlighted = this.state.highlighted || [];
		const triggers = this.props.triggers || [];
		const nodes = data.nodes
			.filter( n => {
				if ( known[n.data.id ]) return false;
				known[n.data.id] = true;
				return true;
			}).map( (n,i) => {
				id2indexMap[n.data.id] = i;
				if ( n.data.type !== 'Soggetto' && ! nodeTypes.includes( n.data.type )) nodeTypes.push( n.data.type );
				if ( n.data.subject ) {
					id2subjectMap[n.data.id] = n.data.subject;
				} else if ( data.subjects.includes(n.data.id) ) {
					id2subjectMap[n.data.id] = n.data.id;
				}
				n.data.text = `${n.data.type} (${n.data.name})`;
				if ( n.data.level ) {
					if ( n.data.level > maxLevel ) maxLevel = n.data.level;
					if ( highlighted.includes(n.data.id) && n.data.level > currentLevel ) currentLevel = n.data.level;
				}
				if ( triggers.includes(n.data.id) ) n.data.trigger = true;
				return n;
			});
		const nodeById = id => {
			const idx = id2indexMap[id] ?? -1;
			return idx < 0 ? null : nodes[idx];
		}
		const nodeTypeSelected = {};
		nodeTypes.forEach( type => { nodeTypeSelected[type] = true; });
		const edges = data.edges.filter( e => !!( nodeById(e.data.source) && nodeById(e.data.target) ) );
		if ( Array.isArray(this.props.bys) && this.props.bys.length ) {
			edges.forEach( e => e.data.light = true );
			this.props.bys.forEach( b => {
				if ( nodeById(b.source) && nodeById(b.target)) edges.push({ data: { source: b.source, target: b.target, fall: true } });
			});
		}
		const foreignEdges = [];
		edges.forEach( e => {
			const ss = id2subjectMap[e.data.source];
			const ts = id2subjectMap[e.data.target];
			if ( ss === ts ) return;
			e.data.foreign = true;
			const sn = nodeById( e.data.source);
			const tn = nodeById( e.data.target );
			sn.data.foreign = true;
			foreignEdges.push({
				source: e.data.source,
				sourceLevel: sn.data.level,
				sourceSubject: ss,
				target: e.data.target,
				targetLevel: tn.data.level,
				targetSubject: ts,
				name: sn.data.type
			});
		});
		let savedNodeTypes;
		const viewSubjects = [];
		data.subjects.forEach( subjectID => {
			const s = nodeById( subjectID );
			viewSubjects.push({
				name: s.data.name,
				visible: this.props.subjects && this.props.subjects.length ? this.props.subjects.includes(subjectID): true,
				id: subjectID
			});
		});
		viewSubjects.sort( ( a, b ) => (a.name.toLocaleLowerCase(this.state.ll).localeCompare(b.name.toLocaleLowerCase(this.state.ll))) );
		if ( this.props.simul ) {
			const saved = getSaved(this.props.simul);
			if ( saved.nodeTypeSelected ) {
				savedNodeTypes = saved.nodeTypeSelected;
			} else {
				saved.nodeTypeSelected = structuredClone(nodeTypes);
			}
			if ( saved.hiddenSubjects && saved.hiddenSubjects.length ) {
				viewSubjects.forEach( s => {
					s.visible = ! saved.hiddenSubjects.includes( s.id );
				})
			}
			if ( typeof saved.level === 'number' ) currentLevel = saved.level;
			setAllSaved(saved);
		}
		this.setState({
			subjects: this.props.subjects ?? data.subjects,
			viewSubjects: viewSubjects,
			nodeTypes: structuredClone(nodeTypes),
			id2subjectMap: id2subjectMap,
			nodeTypeSelected: savedNodeTypes ?? structuredClone(nodeTypes),
			currentSelected: [],
			level: this.props.simul ? currentLevel : this.state.level,
			maxLevel: maxLevel,
			source: {
				subjects: data.subjects,
				nodes: nodes,
				edges: edges,
				id2indexMap: id2indexMap,
				foreignEdges: foreignEdges
			}
		}, this.renderCytoscape );
	}
	getSourceChildren( id ) {
		const nodes = this.state.source?.nodes ?? [];
		const edges = this.state.source?.edges ?? [];
		const id2indexMap = this.state.source?.id2indexMap ?? {};
		const out = [];
		const self = nodes[ id2indexMap[ id ] ];
		edges.filter( edge => edge.data.source === id ).forEach( edge => {
			const idx = id2indexMap[edge.data.target] ?? -1;
			if ( idx < 0 ) return;
			const n = structuredClone(nodes[ idx ] ?? false);
			if ( ! n ) return;
			if ( n?.data?.level !== self.data.level +1 ) return;
			if ( self.data.subject ) {
				if ( n.data.subject !== self.data.subject ) return;
			} else {
				if ( n.data.subject !== self.data.id ) return;
			}
			if ( ! out.find( o => o.data.id === n.data.id ) ) out.push(n);
		});
		return out;
	}
	getSourceParents( id ) {
		if ( (this.state.source.subjects||[]).includes( id )) return [];
		const nodes = this.state.source?.nodes ?? [];
		const edges = this.state.source?.edges ?? [];
		const id2indexMap = this.state.source?.id2indexMap ?? {};
		const out = [];
		const self = nodes[ id2indexMap[ id ] ];
		edges.filter( edge => edge.data.target === id ).forEach( edge => {
			const idx = id2indexMap[edge.data.source] ?? -1;
			if ( idx < 0 ) return;
			const n = structuredClone(nodes[ idx ] ?? false);
			if ( ! n ) return;
			if ( n?.data?.level !== self.data.level -1 ) return;
			if ( n.data.subject ) {
				if ( n.data.subject !== self.data.subject ) return;
			} else {
				if ( n.data.id !== self.data.subject ) return;
			}
			if ( ! out.find( o => o.data.id === n.data.id ) ) out.push(n);
		});
		return out;
	}
	parseNodes() {
		return new Promise( resolve => {
			const src = this.state.source;
			const cs = this.state.currentSelected || [];
			const nodeTypeSelected = this.state.nodeTypeSelected;
			const subjects = (()=>{
				if ( this.state.viewSubjects && this.state.viewSubjects.length ) {
					return this.state.viewSubjects.filter( s => s.visible).map( s => s.id );
				}
				return this.state.subjects || this.state.source?.subjects;
			})();
			/** --- nodes --- */
			const nodes = [];
			const known = {};
			const id2indexMap = {};
			/** nodes-1: clone nodes from source, filtering by level and visibility (per selector) */
			const prefilterFunc = this.props.preFilterNodes ?? ( () => true );
			src.nodes.forEach( n => {
				if ( ! prefilterFunc(n) ) return;
				if ( n.data.type === 'Soggetto' ) {
					if (! subjects.includes(n.data.id) ) return;
				} else if ( cs.includes(n.data.id) ) {
					// ok, add
				} else {
					if ( n.data.level > this.state.level ) return;
					if ( n.data.subject && ! subjects.includes( n.data.subject ) ) return;
					if ( ! nodeTypeSelected.includes(n.data.type) ) return;
				}
				const nn = structuredClone(n);
				known[nn.data.id] = true;
				id2indexMap[nn.data.id] = nodes.length;
				if ( cs.includes(nn.data.id)) nn.data.current = true;
				nodes.push( nn );
			});
			/** nodes-2: add to nodes children of currently clicked nodes */
			if ( cs && cs.length ) {
				cs.forEach( c => {
					if ( ! known[c] ) {
						const idx = src.id2indexMap[c] ?? -1;
						if ( idx < 0 ) return;
						const n = structuredClone(src.nodes[ idx ] ?? false);
						if ( ! n ) return;
						known[c] = true;
						n.data.current = true;
						nodes.push( n );
					}
					const children = this.getSourceChildren( c );
					children.forEach( n => {
						if ( ! known[ n.data.id ] && nodeTypeSelected.includes(n.data.type) ) {
							known[ n.data.id ] = true;
							id2indexMap[n.data.id] = nodes.length;
							nodes.push( n );
						}
					});
				})
			}
			/** nodes-3: add selected/deepSelected attributes */
			if ( this.state.highlighted.length > 0 ) {
				const addDeepSelected = (nodeID) => {
					const parentIDs = this.getSourceParents( nodeID ).map( n => n.data.id );
					parentIDs.forEach( parentID => {
						if ( known[ parentID ] ) {
							const parentIndex = id2indexMap[ parentID ];
							if ( nodes[parentIndex].data.deepSelected || nodes[parentIndex].data.selected ) return;
							nodes[parentIndex].data.deepSelected = true;
						}
						addDeepSelected( parentID );
					})
				};
				this.state.highlighted.forEach( nodeID => {
					if ( known[ nodeID ] ) {
						const n = nodes[ id2indexMap[ nodeID ] ];
						delete n.data.deepSelected;
						if ( typeof this.props.selected === 'object' && typeof this.props.selected[nodeID] === 'object') {
							n.data.status = this.props.selected[nodeID];
						}
						n.data.selected = true;
					}
					addDeepSelected(nodeID);
				});
			}
			this.setState({
				data: {
					nodes: nodes,
					known: known,
					id2indexMap: id2indexMap
				}
			}, resolve );
		});
	}
	customParseNodes() {}
	postParseNodes() {
		return new Promise( resolve => {
			const nodes = this.state.data?.nodes ?? [];
			let levels = [1];
			nodes.filter( n => typeof n.data.level === 'number').forEach( n => {
				if ( ! levels.includes( n.data.level ) ) levels.push( n.data.level );
			});
			levels = levels.sort();
			nodes.forEach( n => {
				if ( typeof n.data.level !== 'number' ) return;
				n.data.level = levels.findIndex( x => x === n.data.level ) +1;
			});
			const data = this.state.data;
			data.nodes = nodes;
			this.setState( { data: data }, resolve );
		});
	}
	parseEdges() {
		return new Promise( resolve => {
			const src = this.state.source;
			const nodes = this.state.data?.nodes || [];
			const id2indexMap = this.state.data?.id2indexMap || {};
			const known = this.state.data?.known || {};
			/** --- edges --- */
			const edges = [];
			/** edges-1 - clone only edges for known nodes */
			src.edges
				.filter( e => !!(known[e.data.source] && known[e.data.target]))
				.forEach( e => {
					//if ( edges.some( ee => (( ee.data.source === e.data.source) && (ee.data.target === e.data.target)))) return;
					edges.push( e );
				});
			/** edges-2 - add "indirect" relationships */
			const getClosestKnownParentID = id => {
				const parents = this.getSourceParents( id );
				const knownParents = parents.filter( n => !! known[n.data.id] ).map( n => n.data.id );
				if ( knownParents.length ) return knownParents[0];
				for ( const p of parents ) {
					const pp = getClosestKnownParentID( p.data.id );
					if ( pp ) return pp;
				}
				return null;
			}
			nodes.forEach( n => {
				const directEdges = edges.filter( e => (e.data.target === n.data.id && known[e.data.source]) );
				if ( directEdges.length > 0 ) {
					const directParents = directEdges
						.map( e => nodes[ id2indexMap[e.data.source] ] )
						.filter( p => (p.data.level === n.data.level -1));
					if ( directParents.length ) return;
				}
				const parentID = getClosestKnownParentID(n.data.id);
				if ( parentID ) {
					edges.push( { data: { id: Glob.generateUid(), source: parentID, target: n.data.id, indirect: true } } );
				}
			});
			const inserted = {};
			/** edges-3 - add deep indirect relationships to different subjects */
			src.nodes.filter( n => n.data.foreign && ! known[n.data.id]).forEach( n => {
				const foreignEdges = src.edges.filter( e => (e.data.source === n.data.id && e.data.foreign) );
				const parentID = getClosestKnownParentID(n.data.id);
				if ( ! parentID ) return;
				foreignEdges.forEach( foreignEdge => {
					const tgt = foreignEdge.data.target;
					if ( inserted[`${parentID} ${tgt}`] || ! known[tgt] ) return;
					inserted[`${parentID} ${tgt}`] = true;
					const edge = structuredClone(foreignEdge);
					edge.data.indirect = true;
					edge.data.deep = true;
					if ( foreignEdge.data.fall ) edge.data.fall = true;
					delete edge.data.name;
					edge.data.source = parentID;
					edges.push( edge );
				});
			});
			const data = this.state.data;
			data.edges = edges;
			data.known = known;
			this.setState({ data: data }, resolve );
		});
	}
	customParseEdges() {}
	postParseEdges() {}
	async parse() {
		await this.parseNodes();
		await this.customParseNodes();
		await this.parseEdges();
		await this.customParseEdges();
		await this.postParseNodes();
		await this.postParseEdges();
	}
	async renderCytoscape() {
		await this.parse();
		const cy = cytoscape({
			container: window.document.getElementById(this.uid),
			panningEnabled: true,
			userPanningEnabled: true,
			elements: {
				nodes: this.state.data?.nodes ?? [],
				edges: this.state.data?.edges ?? [],
			},
			style: this.state.csStyleSheet,
			layout: {
				name: 'concentric',
				fit: true,
				avoidOverlap: (this.state.level < 8),
				concentric: node => (this.state.maxLevel - node.data('level')),
				levelWidth: node=> node.data('level'),
				spacingFactor: this.state.level > 4 ? 1.5 : 2
			}
		});
		cy.on('dblclick', 'node', (evt) => {
			this.onNodeDblClick(evt.target);
		});
		cy.on('cxttap', 'node', (evt) => {
			this.onNodeRightClick(evt.target);
		});
		cy.on('click', 'node', (evt) => {
			this.onNodeClick(evt.target);
		});
		cy.on('drag', 'node', (evt) => {
			const node = evt.target;
			const p = this.state.nodePosition || {};
			p[node.data().id] = node.position();
			if ( this.props.simul ) saveItem( this.props.simul, 'nodePosition', p );
			this.setState({ nodePosition: p });
		});
		const p = this.state.nodePosition || {};
		Object.entries(p).forEach(([id,pos]) => {
			const node = cy.getElementById(id);
			if ( node ) {
				node.position(pos)
			} else {
				delete p[id];
			}
		});
		this.setState({ cytoscape: cy, nodePosition: p } );
	}
	levelSelector() {
		if ( ! this.state.cytoscape ) return null;
		return (
			<InputGroup
				className={prefix+'Slider'}
				key={ this.state.maxLevel }
			>
				<Button
					color={ "primary" }
					disabled={ this.state.level < 2 }
					onClick={ ()=>{
						if ( this.props.simul ) saveItem( this.props.simul, 'level', this.state.level -1 );
						this.setState({ level: this.state.level -1 }, this.renderCytoscape);
					}}
				>
					<ChevronLeftIcon />
				</Button>
				<InputGroupText>
					<Input
						type={"range"}
						min={1}
						max={Math.max(this.state.maxLevel,3)}
						value={this.state.level}
						onChange={ e => {
							if ( this.props.simul ) saveItem( this.props.simul, 'level', Number(e.target.value) );
							this.setState({ level: Number(e.target.value) }, this.renderCytoscape);
						}}
					/>
				</InputGroupText>
				<Button
					color={ this.state.level < 4 ? "primary" : ( this.state.level < 7 ? "warning" : "danger" ) }
					disabled={ this.state.level >= Math.max(this.state.maxLevel,3) }
					onClick={ ()=>{
						if ( this.props.simul ) saveItem( this.props.simul, 'level', this.state.level +1 );
						this.setState({ level: this.state.level +1 }, this.renderCytoscape);
					}}
				>
					<ChevronRightIcon />
				</Button>
			</InputGroup>
		);
	}
	async onNodeClick(node ) {
		if ( typeof this.props.onNodeClick === 'function' ) {
			const continueHandle = await this.props.onNodeClick(node, this);
			if ( ! continueHandle ) return;
		}
		//const nodeData = node.data();
		//console.log( "nodeData", nodeData );
	}
	async onNodeDblClick(node ) {
		if ( typeof this.props.onNodeDblClick === 'function' ) {
			const continueHandle = await this.props.onNodeDblClick(node, this);
			if ( ! continueHandle ) return;
		}
		const i = this.state.infoForNode || {};
		const n = node.data();
		i[n.id] = n;
		//console.log( i );
		this.setState({ infoForNode: i });
	}
	async onNodeRightClick(node ) {
		if ( typeof this.props.onNodeRightClick === 'function' ) {
			const continueHandle = await this.props.onNodeRightClick(node, this);
			if ( ! continueHandle ) return;
		}
		this.nodeElementSelected(node);
	}
	nodeElementSelected( node ) {
		const nodeData = node.data();
		if ( ! nodeData?.id ) return;
		let cs = this.state.currentSelected || [];
		if ( cs.includes( nodeData.id)) {
			cs = cs.filter( c => c !== nodeData.id);
		} else if ( this.state.multiSelect ) {
			cs.push( nodeData.id )
		} else {
			cs = [nodeData.id];
		}
		const p = this.state.nodePosition || {};
		p[nodeData.id] = node.position();
		if ( this.props.simul ) saveItem( this.props.simul, 'nodePosition', p );
		this.setState( { currentSelected : cs, nodePosition: p }, this.renderCytoscape );
	}
	nodeTypeSelect( nodeType, status ) {
		let selected = this.state.nodeTypeSelected;
		if ( status ) {
			if (!selected.includes(nodeType)) selected.push(nodeType);
		} else {
			selected = selected.filter( e => e !== nodeType );
		}
		if ( this.props.simul ) saveItem( this.props.simul, 'nodeTypeSelected', selected );
		this.setState({ nodeTypeSelected: selected }, this.renderCytoscape);
	}
	toggleSubjectVisibility( subjectID ) {
		const vs = this.state.viewSubjects || [];
		const idx = vs.findIndex( s => s?.id === subjectID );
		if ( idx > -1 ) {
			vs[idx].visible = !vs[idx].visible;
			if ( this.props.simul ) {
				const hidden = vs.filter( s => s.visible === false ).map( s => s.id );
				saveItem( this.props.simul, 'hiddenSubjects', hidden );
			}
			this.setState({ viewSubjects: vs }, this.renderCytoscape);
		}
	}
	nodeTypeSelector() {
		if ( ! this.state.nodeTypes || this.state.nodeTypes.length === 0) return null;
		const selected = this.state.nodeTypeSelected;
		const show = !! this.state.showNodeTypeSelector;
		const tab = this.state.showNodeTypeSelectorTab || 'nodes';
		return (
			<div className={prefix+'TypeSelector '+(show?'expanded':'collapsed')}>
				<div className={"toggler"}>
					<div onClick={ ()=>{ this.setState({ showNodeTypeSelector: !show }) }}>
						{ show ? <CloseIcon /> : <TextLabel value={"Filter"} /> }
					</div>
				</div>
				{ show && (
					<div className={"area"}>
						<Nav tabs>
							<Navbar>
								<NavItem>
									<NavLink
										className={ tab === 'nodes' ? 'active' : 'inactive'}
										onClick={ ()=>{ this.setState({ showNodeTypeSelectorTab: 'nodes' }) } }
									>
										<TextLabel value={"Nodes"} />
									<Fragment></Fragment>
								</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={ tab === 'subjects' ? 'active' : 'inactive'}
										onClick={ ()=>{ this.setState({ showNodeTypeSelectorTab: 'subjects' }) } }
									>
										<TextLabel value={"Subjects"} />
									</NavLink>
								</NavItem>
							</Navbar>
						</Nav>
						<TabContent>
							{ tab === 'nodes' ? (
								<TabPane className={"list nodes"}>
									{ this.state.nodeTypes.map( nt=>(
										<InputGroup
											key={ nt }
											className={ nt }
											onClick={ ()=> {
												this.nodeTypeSelect( nt, !selected.includes( nt ) )
											}}
										>
											<InputGroupText>
												<span style={{ backgroundColor: nodeColors[nt] ?? nodeColors.default }} />
												{ selected.includes(nt) ? <AppCheckboxOnIcon /> : <AppCheckboxOffIcon /> }
											</InputGroupText>
											<InputGroupText>
												{ nt.replace(/([a-z])([A-Z])/g,"$1 $2") }
											</InputGroupText>
										</InputGroup>
									))}
								</TabPane>
							) : (
								<TabPane className={"list subs"}>
									{ (this.state.viewSubjects || []).map( s => (
										<InputGroup
											key={ s.id }
											onClick={ ()=> { this.toggleSubjectVisibility( s.id )}}
										>
											<InputGroupText>
												{ s.visible ? <AppCheckboxOnIcon /> : <AppCheckboxOffIcon /> }
											</InputGroupText>
											<InputGroupText>
												{ s.name }
											</InputGroupText>
										</InputGroup>
									))}
								</TabPane>
							) }
						</TabContent>
					</div>
				)}
			</div>
		);
	}
	customRender() {
		const hasSelection = (this.state.currentSelected && this.state.currentSelected.length > 0);
		const hasPositions = Object.keys( this.state.nodePosition || {} ).length > 0;
		const hasHidden = (getSaved( this.props.simul ).hiddenSubjects || []).length > 0;
		return (
			<>
				{ ( hasSelection || hasPositions || hasHidden ) && (
					<div className={"breadcrumbs"}>
						{ (hasPositions||hasHidden) && (
							<div>
								<span
									onClick={ () => {
										dropSaved();
										this.setState({ nodePosition: {} }, this.renderCytoscape)
									}}
								>
									<TextLabel value={"Reset view"} />
								</span>
							</div>
						)}
						{ hasSelection && (
							<div>
								<span
									onClick={ () => this.setState({ currentSelected: null }, this.renderCytoscape) }
								>
									<TextLabel value={"Deselect"} />
								</span>
							</div>
						)}
					</div>
				)}
				{this.levelSelector()}
				{(typeof this.props.nav?.canPrevious === 'function' && this.props.nav?.canPrevious()) && (
					<div className={"stepNav previous"} onClick={ this.props.nav.gotoPrevious }>
						<ChevronLeftIcon />
					</div>
				)}
				{(typeof this.props.nav?.canNext === 'function' && this.props.nav?.canNext()) && (
					<div className={"stepNav next"} onClick={ this.props.nav.gotoNext }>
						<ChevronRightIcon />
					</div>
				)}
			</>
		)
	}
	info() {
		return (
			<div
				className={"info"}
				onClick={ (e) => { e.stopPropagation() } }
			>
				{ this.customInfo() }
				<h4 onClick={ this.toggleInfo }>
					<div>
						<AppInfoIcon />
						<span>Legenda della rappresentazione di archi e nodi</span>
					</div>
					<span>
						<CloseIcon />
					</span>
				</h4>
				<h6>Utilizzare il <strong>click destro</strong> per fare drill su un nodo (selezionarlo), il <strong>doppio click</strong> per visualizzarne le info.</h6>
				<p>Le informazioni riportate sono molteplici, e sono così rappresentate…</p>
				<p>Archi:</p>
				<ul>
					<li>L’<strong>etichetta</strong> è il nome del collegamento da cui è stata dedotta una relazione di dipendenza</li>
					<li>La <strong>punta</strong> dell’arco indica <u>la direzione della relazione di dipendenza</u> che è stata dedotta dal collegamento, <strong>non</strong> quella del collegamento</li>
					<li>Un arco <strong>grigio tratteggiato</strong> indica una <u>relazione profonda</u> o indiretta, che salta alcuni passaggi tra nodo di origine e nodo di destinazione</li>
					<li>Un arco <strong>rosso violaceo</strong> indica una <u>relazione esterna diretta</u>, la relazione tra un nodo e un soggetto diverso dal suo soggetto di appartenenza</li>
					<li>Un arco <strong>arancio tratteggiato</strong> indica una <u>relazione esterna profonda</u>, dovuta a nodi sottostanti rispetto a quelli visibili</li>
				</ul>
				<p>Nodi:</p>
				<ul>
					<li>Uno <strong>spesso bordo rosso</strong> indica che il nodo è stato <u>scelto</u>, o che è <u>caduto</u> (dipendentemente dal tipo di grafo)</li>
					<li>Un <strong>bordo rosso sottile</strong> indica che è stato <u>scelto un sottonodo</u>, o per cui è <u>caduto un sottonodo</u> (dipendentemente dal tipo di grafo)</li>
					<li>Una <strong>opacità leggera</strong> indica che si tratta di un nodo il cui sotto-tipo <u>non è “principale”</u></li>
					<li>Una <strong>evidenziazione in giallo</strong> indica che il nodo è attualmente <u>in drill</u> per la navigazione: ne vengono rappresentati i collegamenti diretti.</li>
				</ul>
			</div>
		)
	}
	customInfo() {
		return null;
	}
	toggleInfo() {
		this.setState({ showInfo: !this.state.showInfo });
	}
	nodeInfo( id, node, idx ) {
		return(
			<PeriGraphicInfo
				key={ id }
				index={ idx }
				node={ node }
				peri={ this.props.id }
				select={ ()=> {
					const n = this.state.cytoscape.getElementById( id );
					console.log( n );
					if ( n ) this.nodeElementSelected(n);
				}}
				close={ ()=>{
					const i = this.state.infoForNode;
					delete this.state.infoForNode[id];
					this.setState({ infoForNode: i });
				}}
			/>
		)
	}
	render() {
		return (
			<div className={prefix+'Container'}>
				<div className={prefix} id={this.uid} />
				<div
					className={prefix+'Info '+(this.state.showInfo?'expanded':'collapsed')}
					onClick={ this.toggleInfo }
				>
					<span className={'toggler'}>
						<AppInfoIcon />
					</span>
					{ this.info() }
				</div>
				{ this.nodeTypeSelector() }
				{ this.customRender() }
				{ (!!this.state.infoForNode) && ( Object.keys(this.state.infoForNode).length > 0) && (
					Object.entries(this.state.infoForNode).map( ([key, value], idx ) => this.nodeInfo(key, value, idx) )
				)}
			</div>
		)
	}
}

export default PeriGraphic;